
  import { getDeviceNameById } from '../../utils/audioDeviceUtils';
  import { getBrowserFingerprint } from '../../utils/browserFingerprint';
  import packageJson from '../../../package.json';

const DEFAULT_AUDIT_SOURCE = 'PORTAL';
const DEFAULT_AUDIT_SEVERITY = 'INFO';

const sanitizeMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return metadata;
  }

  const visited = new WeakSet();
  const replacer = (key, value) => {
    if (value === undefined) {
      return undefined;
    }
    if (value instanceof Error) {
      return {
        message: value.message,
        name: value.name,
      };
    }
    if (typeof value === 'object' && value !== null) {
      if (visited.has(value)) {
        return undefined;
      }
      visited.add(value);
    }
    return value;
  };

  try {
    return JSON.parse(JSON.stringify(metadata, replacer));
  } catch (_err) {
    return undefined;
  }
};

const decorateAuditDescription = (message, auditContext = {}) => {
  const markers = [];

  // Always include app version
  if (packageJson.version) {
    markers.push(`[VERSION:${packageJson.version}]`);
  }

  // Always include browser fingerprint for tracking multi-browser/multi-user scenarios
  try {
    const browserFingerprint = getBrowserFingerprint();
    if (browserFingerprint) {
      markers.push(`[BROWSER:${browserFingerprint}]`);
    }
  } catch (_err) {
    // If fingerprint retrieval fails, continue without it
  }

  // Include login method (SSO vs COGNITO) if available
  const loginMethod = sessionStorage.getItem('loginMethod');
  if (loginMethod) {
    markers.push(`[LOGIN_METHOD:${loginMethod}]`);
  }

  if (!auditContext || typeof auditContext !== 'object') {
    return markers.length ? `${message} ${markers.join(' ')}`.trim() : message;
  }

  const {
    eventType,
    severity,
    source,
    metadata,
  } = auditContext;

  const resolvedSeverity = severity || (eventType || metadata ? DEFAULT_AUDIT_SEVERITY : undefined);
  const resolvedSource = source || (eventType || metadata ? DEFAULT_AUDIT_SOURCE : undefined);
  const cleanMetadata = sanitizeMetadata(metadata);

  if (eventType) {
    markers.push(`[EVENT:${eventType}]`);
  }
  // Add ACTOR from metadata if present (for consultation logs)
  if (cleanMetadata && cleanMetadata.actor) {
    markers.push(`[ACTOR:${cleanMetadata.actor}]`);
  }
  if (resolvedSource) {
    markers.push(`[SOURCE:${resolvedSource}]`);
  }
  if (resolvedSeverity) {
    markers.push(`[SEVERITY:${resolvedSeverity}]`);
  }
  if (cleanMetadata && Object.keys(cleanMetadata).length > 0) {
    try {
      markers.push(`[METADATA:${JSON.stringify(cleanMetadata)}]`);
    } catch (_err) {
      // If serialization fails, skip metadata marker.
    }
  }

  if (!markers.length) {
    return message;
  }

  return `${message} ${markers.join(' ')}`.trim();
};

export const writeAuditLog = async (description = '', action = '', obj = {}, auditContext = {}, options = {}) => {
  const { deferMs = 1500, wait = false } = options;
  const persistAuditLog = async () => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user) {
      console.error('No user data found in session storage');
      return;
    }

    try {
      const token = user.signInUserSession.idToken.jwtToken;
      const user_id = user.username;
      const auditReportObj = {
        user_id: user_id,
        description: description,
        action_name: action,
        parent_account_id: sessionStorage.getItem('parentAccountId'),
        box_id: localStorage.getItem("selectedLocation")
      };

      if (Object.keys(obj).length > 0) {
        if (![null, undefined, ''].includes(obj.userId)) {
          auditReportObj.user_id = obj.userId;
        }

        if (![null, undefined, ''].includes(obj.parentAccountId)) {
          auditReportObj.parent_account_id = obj.parentAccountId;
        }

        if (![null, undefined, ''].includes(obj.boxId)) {
          auditReportObj.box_id = obj.boxId;
        }

        if (![null, undefined, ''].includes(obj.prescriptionMasterId)) {
          auditReportObj.perscription_master_id = obj.prescriptionMasterId;
        }
      }

      let finalDescription = description;

      // Store audio and camera device information for consultation events (for modal display)
      if (action && action.toLowerCase().includes('consultation')) {
        // Get both preferred and selected device IDs for audio devices
        const preferredRingingDevice = localStorage.getItem("preferredConsultRingingDevice") || "";
        const selectedRingingDevice = localStorage.getItem("selectedConsultRingingDevice") || "";
        const preferredCallingDevice = localStorage.getItem("preferredConsultCallingDevice") || "";
        const selectedCallingDevice = localStorage.getItem("selectedConsultCallingDevice") || "";
        const preferredMicInput = localStorage.getItem("preferredConsultMicInput") || "";
        const selectedMicInput = localStorage.getItem("selectedConsultMicInput") || "";

        // Get device names for audio devices
        let preferredRingingName, selectedRingingName, preferredCallingName, selectedCallingName, preferredMicName, selectedMicName;
        try {
          [preferredRingingName, selectedRingingName, preferredCallingName, selectedCallingName, preferredMicName, selectedMicName] = await Promise.all([
            preferredRingingDevice ? getDeviceNameById(preferredRingingDevice, 'output') : Promise.resolve(null),
            selectedRingingDevice ? getDeviceNameById(selectedRingingDevice, 'output') : Promise.resolve("Default System Device"),
            preferredCallingDevice ? getDeviceNameById(preferredCallingDevice, 'output') : Promise.resolve(null),
            selectedCallingDevice ? getDeviceNameById(selectedCallingDevice, 'output') : Promise.resolve("Default System Device"),
            preferredMicInput ? getDeviceNameById(preferredMicInput, 'input') : Promise.resolve(null),
            selectedMicInput ? getDeviceNameById(selectedMicInput, 'input') : Promise.resolve("Default System Device")
          ]);
        } catch (error) {
          console.warn('Failed to get device names, using fallback:', error);
          preferredRingingName = preferredRingingDevice || null;
          selectedRingingName = selectedRingingDevice || "Default System Device";
          preferredCallingName = preferredCallingDevice || null;
          selectedCallingName = selectedCallingDevice || "Default System Device";
          preferredMicName = preferredMicInput || null;
          selectedMicName = selectedMicInput || "Default System Device";
        }

        const audioDeviceInfo = {
          ringingDevice: { 
            preferred: preferredRingingDevice ? { deviceId: preferredRingingDevice, deviceName: preferredRingingName } : null,
            selected: { deviceId: selectedRingingDevice || "default", deviceName: selectedRingingName }
          },
          callingDevice: { 
            preferred: preferredCallingDevice ? { deviceId: preferredCallingDevice, deviceName: preferredCallingName } : null,
            selected: { deviceId: selectedCallingDevice || "default", deviceName: selectedCallingName }
          },
          micInput: { 
            preferred: preferredMicInput ? { deviceId: preferredMicInput, deviceName: preferredMicName } : null,
            selected: { deviceId: selectedMicInput || "default", deviceName: selectedMicName }
          }
        };

        // Get camera device information including both preferred and selected camera
        const preferredCameraId = localStorage.getItem("preferredConsultCamera") || "";
        const selectedCameraId = localStorage.getItem("selectedConsultCamera") || "";
        let cameraDeviceInfo = { available: false, devices: [], preferred: null, selected: null };
        try {
          if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoInputs = devices.filter(d => d.kind === 'videoinput');
            
            // Find the preferred and selected cameras
            const preferredCamera = preferredCameraId 
              ? videoInputs.find(d => d.deviceId === preferredCameraId) 
              : null;
            const selectedCamera = selectedCameraId 
              ? videoInputs.find(d => d.deviceId === selectedCameraId) 
              : null;
            
            cameraDeviceInfo = {
              available: videoInputs.length > 0,
              count: videoInputs.length,
              preferred: preferredCamera 
                ? { deviceId: preferredCamera.deviceId, deviceName: preferredCamera.label || 'Unknown Camera' }
                : (preferredCameraId ? { deviceId: preferredCameraId, deviceName: 'Device Not Found' } : null),
              selected: selectedCamera 
                ? { deviceId: selectedCamera.deviceId, deviceName: selectedCamera.label || 'Unknown Camera' }
                : (selectedCameraId ? { deviceId: selectedCameraId, deviceName: 'Device Not Found' } : null),
              devices: videoInputs.map(d => ({
                deviceId: d.deviceId || 'unknown',
                deviceName: d.label || 'Unknown Camera'
              }))
            };
          }
        } catch (cameraError) {
          console.warn('Failed to get camera device info:', cameraError);
          cameraDeviceInfo = { 
            available: false, 
            error: cameraError.message,
            preferred: preferredCameraId ? { deviceId: preferredCameraId, deviceName: 'Unknown (enumeration failed)' } : null,
            selected: selectedCameraId ? { deviceId: selectedCameraId, deviceName: 'Unknown (enumeration failed)' } : null
          };
        }

        finalDescription = `${description} [AUDIO_DEVICE_INFO:${JSON.stringify(audioDeviceInfo)}] [CAMERA_DEVICE_INFO:${JSON.stringify(cameraDeviceInfo)}]`;
      }
      auditReportObj.description = decorateAuditDescription(finalDescription, auditContext);

      const response = await fetch(`${process.env.REACT_APP_BASEURL}auditreport`, {
        method: 'POST',
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify(auditReportObj)
      });
      const eventResponse = await response.json();
      console.info(`Response from audit-log ${JSON.stringify(eventResponse, null, 2)}`);
      return eventResponse;
    } catch (error) {
      console.error(`Error response from audit-log ${JSON.stringify(error, null, 2)}`);
    }
  };

  if (wait) {
    if (deferMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, deferMs));
    }
    return await persistAuditLog();
  }

  setTimeout(() => {
    persistAuditLog();
  }, deferMs);
};



const updateLiveConsult = async () => {
  const user = sessionStorage.getItem("user");
  if (![undefined, "undefined", null, "null", ""].includes(user)) {
    const parsedUser = JSON.parse(user);
    const payload = {
      userId: parsedUser.username,
      is_live: false,
      is_user_consult: false
    };
    return await fetch(process.env.REACT_APP_BASEURL + `updateIsLive`, {
      method: "PUT",
      headers: {
        Authorization:
          "Bearer " +
          JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
            .jwtToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((response) => {
        return response;
      });
  }
};

export default updateLiveConsult;


// WEBPACK FOOTER //
// ./src/components/common/apis.js